
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Alert, useNotifications } from '@/contexts/NotificationsContext';
import { CheckCircle, AlertTriangle, AlertCircle, Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const NotificationItem: React.FC<{ alert: Alert; onRead: () => void }> = ({ alert, onRead }) => {
  const getIcon = () => {
    if (alert.message.toLowerCase().includes('critical')) {
      return <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />;
    } else if (alert.message.toLowerCase().includes('warning')) {
      return <AlertTriangle className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0" />;
    } else {
      return <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />;
    }
  };

  const deviceTypeColors = {
    energy: 'text-blue-600 dark:text-blue-400',
    gas: 'text-orange-600 dark:text-orange-400',
    water: 'text-teal-600 dark:text-teal-400'
  };

  return (
    <DropdownMenuItem
      className={cn(
        "p-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 last:border-0",
        !alert.read ? "bg-blue-50/80 dark:bg-blue-900/20" : ""
      )}
      onClick={onRead}
    >
      <div className="flex items-start w-full">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium flex items-center gap-1.5">
            <span className={cn("font-semibold", deviceTypeColors[alert.deviceType])}>
              {alert.deviceType.charAt(0).toUpperCase() + alert.deviceType.slice(1)}:
            </span> 
            <span className="truncate">{alert.deviceId}</span>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{alert.message}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 italic">
            {formatDistanceToNow(alert.timestamp, { addSuffix: true })}
          </p>
        </div>
        {!alert.read && (
          <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 animate-pulse">
            New
          </Badge>
        )}
      </div>
    </DropdownMenuItem>
  );
};

const NotificationDropdown: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { alerts, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[380px] max-h-[80vh] overflow-y-auto rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 animate-in fade-in-80 data-[side=bottom]:slide-in-from-top-2 py-2"
      >
        <DropdownMenuLabel className="flex justify-between items-center sticky top-0 bg-white dark:bg-gray-950 z-10 py-3 px-4 border-b border-gray-100 dark:border-gray-800">
          <span className="font-semibold text-lg flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
            {unreadCount > 0 && (
              <Badge className="ml-2 bg-blue-600 text-white hover:bg-blue-700">
                {unreadCount}
              </Badge>
            )}
          </span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              className="text-xs text-blue-600 dark:text-blue-400 h-auto py-1 px-2 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={(e) => {
                e.stopPropagation();
                markAllAsRead();
              }}
            >
              Mark all as read
            </Button>
          )}
        </DropdownMenuLabel>
        
        <DropdownMenuGroup className="max-h-[60vh] overflow-y-auto">
          {alerts.length > 0 ? (
            alerts.map(alert => (
              <NotificationItem 
                key={alert.id} 
                alert={alert} 
                onRead={() => markAsRead(alert.id)} 
              />
            ))
          ) : (
            <div className="py-8 px-4 text-center">
              <div className="w-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                <Bell className="h-8 w-8 mb-2 text-gray-300 dark:text-gray-600" />
                <p className="text-sm">No notifications</p>
                <p className="text-xs mt-1">You're all caught up!</p>
              </div>
            </div>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationDropdown;
